import React from "react";
import ToothSelector from "./ToothSelectorOld";
import SelectInput from "./SelectInputOld";
import TextInput from "./TextInputOld";
import {
  CrownBridgeForm,
  ImplantForm,
  BlueCardProps,
} from "../../../types/BlueCardTypes";
import { useAppSelector } from "../../../store/hooks";
import { isVelvetLabToken } from "../../../utils/brandLogo";

const BlueCard: React.FC<BlueCardProps> = ({
  mode,
  cardData,
  onChange,
  errors,
  filterToken,
}) => {
  const { card } = cardData;
  const { user } = useAppSelector((state) => state.auth);

  const prosthesisOptions =
    mode === "crownBridge"
      ? [
          { value: "Inlay", label: "Inlay" },
          { value: "Onlay / overlay", label: "Onlay / overlay" },
          { value: "Crown", label: "Crown" },
          { value: "Bridge", label: "Bridge" },
          {
            value: "Maryland / Resin bonded bridge",
            label: "Maryland / Resin bonded bridge",
          },
          { value: "Veneer", label: "Veneer" },
        ]
      : [
          { value: "Crown", label: "Crown" },
          { value: "Bridge", label: "Bridge" },
        ];

  return (
    <div className="bg-[#eaf6f8] p-2 w-full">
      <div className="w-[100%] md:w-[88%]">
        {(mode === "crownBridge" || mode === "implant") && (
          <div className="mb-2 flex">
            <label className="block mb-1 w-[35%]">
              Tooth #s<span className="text-red-500">*</span>
              <span className="block text-gray-600 text-xs md:text-sm">
                Select all applicable (including pontics)
              </span>
              {errors?.toothNumbers && (
                <span className="text-red-500 text-md">
                  {errors.toothNumbers}
                </span>
              )}
            </label>

            <div className="w-[65%]">
              <ToothSelector
                toothNumbers={card.toothNumbers}
                onToothClick={(tooth) => {
                  const toothStr = tooth.toString();
                  const updated = card.toothNumbers.includes(toothStr)
                    ? card.toothNumbers.filter((t) => t !== toothStr)
                    : [...card.toothNumbers, toothStr];

                  onChange("toothNumbers", updated);
                }}
              />
            </div>
          </div>
        )}

        {mode === "crownBridge" && (
          <>
            <SelectInput
              label="Material"
              name="material"
              value={(card as CrownBridgeForm).material}
              onChange={(e) => onChange("material", e.target.value)}
              options={[
                ...(user?.partner === "elemdig"
                  ? [
                      { value: "3M", label: "3M" },
                      { value: "Argen HT+", label: "Argen HT+" },
                      { value: "Argen Z Ultra", label: "Argen Z Ultra" },
                      { value: "Bio HPP/Peek", label: "Bio HPP/Peek" },
                      { value: "Brecam", label: "Brecam" },
                      { value: "Celtra Press", label: "Celtra Press" },
                      { value: "Coltene", label: "Coltene" },
                      { value: "Dental Direct", label: "Dental Direct" },
                      { value: "GC Cerasmart", label: "GC Cerasmart" },
                      { value: "Katana", label: "Katana" },
                      { value: "Luxor", label: "Luxor" },
                      { value: "Milled Composite", label: "Milled Composite" },
                      { value: "Milled Emax", label: "Milled Emax" },
                      { value: "Pmma", label: "Pmma" },
                      { value: "Pressed E.Max", label: "Pressed E.Max" },
                      { value: "Pressed Livento", label: "Pressed Livento" },
                      { value: "Sagemax", label: "Sagemax" },
                      { value: "Vita Enamic", label: "Vita Enamic" },
                      { value: "Wax", label: "Wax" },
                      {
                        value: "Whitepeaks Symphony",
                        label: "Whitepeaks Symphony",
                      },
                      { value: "ZirCAD Prime", label: "ZirCAD Prime" },
                      {
                        value: "ZirCAD Prime Esthetic",
                        label: "ZirCAD Prime Esthetic",
                      },
                    ]
                  : [
                      { value: "Composite", label: "Composite" },
                      { value: "Emax", label: "Emax" },
                      { value: "Zirconia", label: "Zirconia" },
                      {
                        value: "Metal (precious/non-precious)",
                        label: "Metal (precious/non-precious)",
                      },
                      { value: "Acrylic / PMMA", label: "Acrylic / PMMA" },
                    ]),
                ...(isVelvetLabToken(filterToken)
                  ? [{ value: "Print", label: "Print" }]
                  : []),
              ]}
            />

            <SelectInput
              label="Prosthesis Type"
              name="prosthesisType"
              value={(card as CrownBridgeForm).prosthesisType}
              onChange={(e) => onChange("prosthesisType", e.target.value)}
              options={prosthesisOptions}
            />

            <SelectInput
              label="Design Type"
              name="designType"
              value={(card as CrownBridgeForm).designType}
              onChange={(e) => onChange("designType", e.target.value)}
              options={[
                { value: "Full Contour", label: "Full Contour" },
                { value: "Facial cutback", label: "Facial cutback" },
                { value: "Coping", label: "Coping" },
              ]}
            />

            <TextInput
              label="Shade"
              name="shade"
              optional
              value={(card as CrownBridgeForm).shade}
              onChange={(e) => onChange("shade", e.target.value)}
            />
          </>
        )}

        {mode === "implant" && (
          <>
            {isVelvetLabToken(filterToken) ? (
              // ---------------- JU Form ----------------
              <>
                <SelectInput
                  label="Restoration required"
                  name="restorationRequired"
                  value={(card as ImplantForm).restorationRequired}
                  onChange={(e) =>
                    onChange("restorationRequired", e.target.value)
                  }
                  options={[
                    {
                      value: "Custom abutment + Crown",
                      label: "Custom abutment + Crown",
                    },
                    {
                      value: "Custom abutment only",
                      label: "Custom abutment only",
                    },
                    { value: "Screw retained", label: "Screw retained" },
                  ]}
                />

                <SelectInput
                  label="Prosthesis Type"
                  name="prosthesisType"
                  value={(card as ImplantForm).prosthesisType}
                  onChange={(e) => onChange("prosthesisType", e.target.value)}
                  options={prosthesisOptions}
                />

                <SelectInput
                  label="Implant System"
                  name="implantSystem"
                  value={(card as ImplantForm).implantSystem}
                  onChange={(e) => onChange("implantSystem", e.target.value)}
                  options={[
                    {
                      value: "IPD Biohorizon",
                      label: "IPD Biohorizon",
                    },
                    {
                      value: "Tri Matrix",
                      label: "Tri Matrix",
                    },
                    { value: "Other", label: "Other" },
                  ]}
                />

                <TextInput
                  label="Implant Size"
                  name="implantSize"
                  value={(card as ImplantForm).implantSize}
                  onChange={(e) => onChange("implantSize", e.target.value)}
                />

                <SelectInput
                  label="Connection Type"
                  name="connectionType"
                  value={(card as ImplantForm).connectionType}
                  onChange={(e) => onChange("connectionType", e.target.value)}
                  options={[
                    {
                      value: "Custom Interface",
                      label: "Custom Interface",
                    },
                    {
                      value: "Direct Interface",
                      label: "Direct Interface",
                    },
                  ]}
                />

                <SelectInput
                  label="Scan Flag Type"
                  name="scanFlagType"
                  value={(card as ImplantForm).scanFlagType}
                  onChange={(e) => onChange("scanFlagType", e.target.value)}
                  options={[
                    {
                      value: "Engaging",
                      label: "Engaging",
                    },
                    {
                      value: "Non-Engaging",
                      label: "Non-Engaging",
                    },
                    {
                      value: "Multi",
                      label: "Multi",
                    },
                  ]}
                />

                <SelectInput
                  optional
                  label="Tissue pressure"
                  name="tissuePressure"
                  value={(card as ImplantForm).tissuePressure}
                  onChange={(e) => onChange("tissuePressure", e.target.value)}
                  options={[
                    {
                      value: "Zero (Modified Ridge Lap)",
                      label: "Zero (Modified Ridge Lap)",
                    },
                    {
                      value: "Ovate",
                      label: "Ovate",
                    },
                  ]}
                />

                <SelectInput
                  optional
                  label="Screw hole Diameter"
                  name="screwHoleDiameter"
                  value={(card as ImplantForm).screwHoleDiameter}
                  onChange={(e) =>
                    onChange("screwHoleDiameter", e.target.value)
                  }
                  options={[
                    {
                      value: "Default",
                      label: "Default",
                    },
                    {
                      value: "3mm",
                      label: "3mm",
                    },
                  ]}
                />

                <SelectInput
                  optional
                  label="Screw hole angulation"
                  name="screwHoleAngulation"
                  value={(card as ImplantForm).screwHoleAngulation}
                  onChange={(e) =>
                    onChange("screwHoleAngulation", e.target.value)
                  }
                  options={[
                    {
                      value: "0-15",
                      label: "0-15",
                    },
                    {
                      value: "More than 15",
                      label: "More than 15",
                    },
                  ]}
                />

                <SelectInput
                  optional
                  label="Bridge base width"
                  name="bridgeBaseWidth"
                  value={(card as ImplantForm).bridgeBaseWidth}
                  onChange={(e) => onChange("bridgeBaseWidth", e.target.value)}
                  options={[
                    {
                      value: "As per pontic natural size",
                      label: "As per pontic natural size",
                    },
                    {
                      value: "8-10mm",
                      label: "8-10mm",
                    },
                    {
                      value: "12-13mm",
                      label: "12-13mm",
                    },
                  ]}
                />

                <SelectInput
                  optional
                  label="All on X design with gingiva"
                  name="allOnXDesignWithGingiva"
                  value={(card as ImplantForm).allOnXDesignWithGingiva}
                  onChange={(e) =>
                    onChange("allOnXDesignWithGingiva", e.target.value)
                  }
                  options={[
                    {
                      value: "Needs gingiva",
                      label: "Needs gingiva",
                    },
                    {
                      value: "Does not need gingiva",
                      label: "Does not need gingiva",
                    },
                  ]}
                />

                <SelectInput
                  optional
                  label="Model gingiva for all on X cases"
                  name="modelGingivaForAllOnXCases"
                  value={(card as ImplantForm).modelGingivaForAllOnXCases}
                  onChange={(e) =>
                    onChange("modelGingivaForAllOnXCases", e.target.value)
                  }
                  options={[
                    {
                      value: "One piece",
                      label: "One piece",
                    },
                    {
                      value: "Multiple pieces",
                      label: "Multiple pieces",
                    },
                  ]}
                />
              </>
            ) : (
              // ---------------- Common Implant Form ----------------
              <>
                <TextInput
                  label="Scan body manufacturer"
                  name="scanBodyManufacturer"
                  value={(card as ImplantForm).scanBodyManufacturer}
                  onChange={(e) =>
                    onChange("scanBodyManufacturer", e.target.value)
                  }
                />
                <TextInput
                  optional
                  label="Scan body code"
                  name="scanBodyCode"
                  value={(card as ImplantForm).scanBodyCode}
                  onChange={(e) => onChange("scanBodyCode", e.target.value)}
                />
                <TextInput
                  label="Implant manufacturer"
                  name="implantManufacturer"
                  value={(card as ImplantForm).implantManufacturer}
                  onChange={(e) =>
                    onChange("implantManufacturer", e.target.value)
                  }
                />
                <TextInput
                  label="Platform diameter"
                  name="platformDiameter"
                  value={(card as ImplantForm).platformDiameter}
                  onChange={(e) => onChange("platformDiameter", e.target.value)}
                />
                <SelectInput
                  label="Restoration required"
                  name="restorationRequired"
                  value={(card as ImplantForm).restorationRequired}
                  onChange={(e) =>
                    onChange("restorationRequired", e.target.value)
                  }
                  options={[
                    {
                      value: "Custom abutment + Crown",
                      label: "Custom abutment + Crown",
                    },
                    {
                      value: "Custom abutment only",
                      label: "Custom abutment only",
                    },
                    { value: "Screw retained", label: "Screw retained" },
                  ]}
                />
                <SelectInput
                  label="Prosthesis Type"
                  name="prosthesisType"
                  value={(card as ImplantForm).prosthesisType}
                  onChange={(e) => onChange("prosthesisType", e.target.value)}
                  options={prosthesisOptions}
                />
                <SelectInput
                  label="Material"
                  name="material"
                  value={(card as ImplantForm).material}
                  onChange={(e) => onChange("material", e.target.value)}
                  options={[
                    ...(user?.partner === "elemdig"
                      ? [
                          { value: "3M", label: "3M" },
                          { value: "Argen HT+", label: "Argen HT+" },
                          { value: "Argen Z Ultra", label: "Argen Z Ultra" },
                          { value: "Bio HPP/Peek", label: "Bio HPP/Peek" },
                          { value: "Brecam", label: "Brecam" },
                          { value: "Celtra Press", label: "Celtra Press" },
                          { value: "Coltene", label: "Coltene" },
                          { value: "Dental Direct", label: "Dental Direct" },
                          { value: "GC Cerasmart", label: "GC Cerasmart" },
                          { value: "Katana", label: "Katana" },
                          { value: "Luxor", label: "Luxor" },
                          {
                            value: "Milled Composite",
                            label: "Milled Composite",
                          },
                          { value: "Milled Emax", label: "Milled Emax" },
                          { value: "Pmma", label: "Pmma" },
                          { value: "Pressed E.Max", label: "Pressed E.Max" },
                          {
                            value: "Pressed Livento",
                            label: "Pressed Livento",
                          },
                          { value: "Sagemax", label: "Sagemax" },
                          { value: "Vita Enamic", label: "Vita Enamic" },
                          { value: "Wax", label: "Wax" },
                          {
                            value: "Whitepeaks Symphony",
                            label: "Whitepeaks Symphony",
                          },
                          { value: "ZirCAD Prime", label: "ZirCAD Prime" },
                          {
                            value: "ZirCAD Prime Esthetic",
                            label: "ZirCAD Prime Esthetic",
                          },
                        ]
                      : [
                          { value: "Composite", label: "Composite" },
                          { value: "Emax", label: "Emax" },
                          { value: "Zirconia", label: "Zirconia" },
                          {
                            value: "Metal (precious/non-precious)",
                            label: "Metal (precious/non-precious)",
                          },
                          { value: "Acrylic / PMMA", label: "Acrylic / PMMA" },
                        ]),
                  ]}
                />
                <TextInput
                  label="Shade"
                  name="shade"
                  optional
                  value={(card as ImplantForm).shade}
                  onChange={(e) => onChange("shade", e.target.value)}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlueCard;
